import * as React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import {
  hasRightsConsent,
  requireRightsConsent,
  settleRightsConsent,
  subscribeRightsConsent,
} from "./rightsConsent";

// Mounted once in App. Owns the "I have the rights" dialog and the page-wide
// listeners that hold back any file picker or drop inside [data-consent-tool]
// until it is ticked (see rightsConsent.ts).
export function RightsConsentHost() {
  const { t } = useTranslation();
  const [tool, setTool] = React.useState<string | null>(null);
  const [checked, setChecked] = React.useState(false);

  React.useEffect(
    () =>
      subscribeRightsConsent((p) => {
        setTool(p ? p.tool : null);
        setChecked(false);
      }),
    [],
  );

  React.useEffect(() => {
    const toolOf = (el: EventTarget | null) =>
      el instanceof Element ? el.closest<HTMLElement>("[data-consent-tool]")?.dataset.consentTool || null : null;

    const onClick = (e: MouseEvent) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || input.type !== "file") return;
      const name = toolOf(input);
      if (!name || hasRightsConsent(name)) return;
      e.preventDefault();
      e.stopPropagation();
      void requireRightsConsent(name, () => input.click());
    };

    const onDrop = (e: DragEvent) => {
      const name = toolOf(e.target);
      const files = e.dataTransfer?.files;
      if (!name || !files || files.length === 0 || hasRightsConsent(name)) return;
      e.preventDefault();
      e.stopPropagation();
      const target = e.target as Element;
      const kept = Array.from(files);
      void requireRightsConsent(name, () => {
        try {
          const dt = new DataTransfer();
          kept.forEach((f) => dt.items.add(f));
          target.dispatchEvent(new DragEvent("drop", { bubbles: true, cancelable: true, dataTransfer: dt }));
        } catch {
          /* the visitor drops again */
        }
      });
    };

    document.addEventListener("click", onClick, true);
    document.addEventListener("drop", onDrop, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("drop", onDrop, true);
    };
  }, []);

  React.useEffect(() => {
    if (!tool) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") settleRightsConsent(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [tool]);

  if (!tool || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/70 p-4"
      style={{ zIndex: 2147483000 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) settleRightsConsent(false);
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="rights-consent-title"
        data-testid="rights-consent-dialog"
        className="w-full max-w-md rounded-xl border border-border bg-background p-6 text-foreground shadow-2xl"
      >
        <h2 id="rights-consent-title" className="text-lg font-semibold">
          {t("rightsConsent.title")}
        </h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{t("rightsConsent.body")}</p>
        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3">
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => setChecked(v === true)}
            className="mt-0.5"
            data-testid="rights-consent-checkbox"
            autoFocus
          />
          <span className="text-sm leading-6">
            {t("rightsConsent.checkbox")}{" "}
            <a href="/terms" target="_blank" rel="noreferrer" className="underline">
              {t("rightsConsent.termsLink")}
            </a>
          </span>
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-border px-4 py-2 text-sm"
            onClick={() => settleRightsConsent(false)}
          >
            {t("rightsConsent.cancel")}
          </button>
          <button
            type="button"
            disabled={!checked}
            data-testid="rights-consent-confirm"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => settleRightsConsent(true)}
          >
            {t("rightsConsent.confirm")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
